import * as React from 'react';

import {
    FlexyButton,
    useAvatarUrl,
    useAvatarUrlDispatch,
    useModal,
} from '@europrocurement/flexy-components';
import { Box, CircularProgress, Container, SxProps, Typography, useTheme } from '@mui/material';
import { useUtilisateurService } from '@europrocurement/l2d-domain';

import {
    errorNotification,
    successNotification,
} from '@europrocurement/flexy-components/components/atoms/NotificationToasters';
import { KeycloakIsAuth, UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { AvatarsApiApiAvatarsPostRequest } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import sha256 from '@europrocurement/l2d-utils/function-utils/sha256';

import { ValidationModal } from '../../../ValidationModal/ValidationModal';
import { AvatarCompactEditor } from '../AvatarCompactEditor/AvatarCompactEditor';

export type ManageAvatarProps = {
    overwriteUser?: {
        firstName?: string;
        lastName?: string;
        username?: string;
        email?: string;
    };
    sxContainer?: SxProps;
    sxAvatar?: SxProps;
};

/**
 * Gestion component for the user avatar. Only authorized users can edit the avatar.
 * @param user - the user who will be linked with the avatar
 * @returns gestion component for the user avatar
 */
export const ManageAvatar: React.FunctionComponent<ManageAvatarProps> = function ({
    overwriteUser,
    sxContainer,
    sxAvatar,
}) {
    const { avatarsApi, avatarBaseUrl } = useUtilisateurService();
    const keycloak = UseKeycloakService();
    const { modalActions } = useModal();
    const { palette } = useTheme();

    const [keycloakIdHash, setKeycloakIdHash] = React.useState<string | undefined>(undefined);

    const { avatarUrl, usersAvatarInfos } = useAvatarUrl();
    const avatarUrlDispatch = useAvatarUrlDispatch();
    const userIdentifier: string | undefined = keycloak.getTokenParsed().preferred_username;
    let currentUserIndex: number | undefined;

    let canEdit = false;

    if (keycloak.getEmail()) {
        canEdit = true;
    }

    const connectedUser = {
        firstName: keycloak.getName().split(' ')[0],
        lastName: keycloak.getName().split(' ')[1],
        email: keycloak.getEmail(),
        username: keycloak.getTokenParsed().preferred_username,
    };

    const user = overwriteUser || connectedUser;

    React.useEffect(() => {
        // get the hashed username to find the avatar
        if (user.username !== undefined && user.username !== null && user.username !== '') {
            sha256(user.username).then(setKeycloakIdHash);

            // update the context for the logged user only
            if (
                avatarUrl === undefined &&
                keycloakIdHash !== undefined &&
                userIdentifier === user.username
            ) {
                avatarUrlDispatch({
                    type: 'update',
                    payload: `${avatarBaseUrl + keycloakIdHash}?time=${new Date().getTime()}`,
                    list: usersAvatarInfos,
                });
            }

            // in case the updated avatar is an other user avatar than the logged one
            if (userIdentifier !== user.username && keycloakIdHash !== undefined) {
                avatarUrlDispatch({
                    type: 'update',
                    payload: avatarUrl,
                    list: usersAvatarInfos,
                });
            }
        }
    }, [
        user.username,
        keycloakIdHash,
        avatarBaseUrl,
        avatarUrl,
        userIdentifier,
        avatarUrlDispatch,
        usersAvatarInfos,
    ]);

    if (!user.username) {
        errorNotification(
            "Impossible de gérer l'avatar car l'utilisateur n'a pas d'identifiant Keycloak",
        );
        return <Box />;
    }

    if (!keycloakIdHash) {
        return <CircularProgress />;
    }

    if (usersAvatarInfos !== undefined) {
        currentUserIndex = usersAvatarInfos.findIndex(
            (element) => element.avatarUserIdentifier === user.username,
        );
    }
    /**
     * On avatar upload, get the image file. If the file is < 300Ko, send it to the API to store it into S3.
     * @param event onChange : get the upload image
     */
    const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && user.username) {
            // max file size 300Ko
            if (file.size < 301000) {
                const params: AvatarsApiApiAvatarsPostRequest = {
                    avatar: file,
                    userIdentifier: user.username,
                };

                avatarsApi
                    .apiAvatarsPost(params)
                    .then(() => {
                        successNotification('Votre avatar a bien été ajouté.');

                        // check whose avatar is modified to update the right url
                        if (user.username === userIdentifier) {
                            avatarUrlDispatch({
                                type: 'update',
                                payload: `${avatarBaseUrl + keycloakIdHash}?time=${new Date().getTime()}`,
                                list: usersAvatarInfos,
                            });
                        } else if (currentUserIndex !== undefined && usersAvatarInfos) {
                            avatarUrlDispatch({
                                type: 'update',
                                list: [
                                    ...usersAvatarInfos.map((userAvatar, index) => {
                                        if (index === currentUserIndex) {
                                            return {
                                                ...userAvatar,
                                                currentUserAvatarUrl: `${avatarBaseUrl}${keycloakIdHash}?time=${new Date().getTime()}`,
                                            };
                                        }
                                        return userAvatar;
                                    }),
                                ],
                            });
                        }
                    })
                    .catch((error) => {
                        errorNotification("Une erreur est survenue lors de l'ajout de l'image");
                        console.error(error);
                    });
            } else {
                errorNotification('Votre image doit être inférieure à 300 Ko.');
            }
        }
    };

    const deleteAvatar = () => {
        modalActions.close();

        if (user.username) {
            avatarsApi
                .apiAvatarsTitleDelete({ title: user.username })
                .then(() => {
                    successNotification('Votre avatar a bien été supprimé.');

                    // check whose avatar is modified to update the right url
                    if (user.username === userIdentifier) {
                        avatarUrlDispatch({
                            type: 'reset',
                        });
                    } else if (usersAvatarInfos !== undefined) {
                        avatarUrlDispatch({
                            type: 'update',
                            list: [
                                ...usersAvatarInfos.map((userAvatar, index) => {
                                    if (index === currentUserIndex) {
                                        return {
                                            ...userAvatar,
                                            currentUserAvatarUrl: undefined,
                                        };
                                    }
                                    return userAvatar;
                                }),
                            ],
                        });
                    }
                })
                .catch((error) => {
                    errorNotification("Une erreur est survenue lors de la suppression de l'image");
                    console.error(error);
                });
        }
    };

    /**
     * On click on bin icon, open a confirmation modal. If the deletion is confirmed, delete the image of the user into S3 (API request).
     * @param event onClick : click on bin icon
     */
    const handleAvatarDeletion = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();

        modalActions.call(
            <ValidationModal
                title="Confirmer la suppression"
                message="Voulez-vous vraiment supprimer cette image ? Cette action est définitive."
                confirmationLabel="Oui, définitivement supprimer l'image"
                onSubmit={deleteAvatar}
                isDestructive
            />,
        );
    };

    const getSourceUrl = () => {
        if (userIdentifier !== user.username) {
            if (usersAvatarInfos && currentUserIndex !== undefined) {
                const source = usersAvatarInfos[currentUserIndex].currentUserAvatarUrl;
                return source;
            }
            return `${avatarBaseUrl}${keycloakIdHash}`;
        }
        return avatarUrl;
    };

    return (
        <Container
            sx={{
                marginLeft: '15px',
                ...sxContainer,
            }}
        >
            <KeycloakIsAuth
                elseChildren={
                    <Typography>
                        Vous n&apos;êtes pas authentifié
                        <FlexyButton
                            label="Login"
                            variant="contained"
                            color={palette.primary}
                            onClick={() => keycloak.getKc().login()}
                        />
                    </Typography>
                }
            >
                <AvatarCompactEditor
                    canEdit={canEdit}
                    firstName={user.firstName}
                    lastName={user.lastName}
                    avatarSrc={getSourceUrl()}
                    handleAvatarChange={handleAvatarChange}
                    handleAvatarDeletion={handleAvatarDeletion}
                    sx={sxAvatar}
                />
            </KeycloakIsAuth>
        </Container>
    );
};
